// Backend service for measurements
// In production, nginx proxies /api/* to the backend container
// In development, use localhost:5126 directly
import { apiPost, apiGet, apiPut, apiDelete } from '../utils/apiClient';

export const MEASUREMENT_API_BASE = process.env.REACT_APP_API_URL || '/api/measurement';

const API_BASE_URL = MEASUREMENT_API_BASE;

export interface Creative {
  id: string;
  name: string;
  type: 'image' | 'video' | 'other';
  url: string; // Generated URL for preview
  externalId: string;
}

export interface BrandClassification {
  brandPosition: string;
  brandScope: string;
  businessType: string;
  purchaseCycle: string;
  industrySector: string;
  industrySubcategory: string;
}

export interface BrandStandings {
  awareness: number;
  consideration: number;
  preference: number;
  actionIntent: number;
}

export interface BrandLifts {
  awareness: number;
  consideration: number;
  preference: number;
  actionIntent: number;
}

export interface FunnelValues {
  awareness: number;
  consideration: number;
  preference: number;
  actionIntent: number;
}

export interface Benchmark {
  name: string;
  funnel: FunnelValues;
  absolute: FunnelValues;
  /** Executive summary text for this benchmark; from API. */
  executiveSummaryText?: string | null;
}

export type MeasurementStatus =
  | 'creating'
  | 'processing'
  | 'upcoming'
  | 'active'
  | 'reported'
  | 'failed';

export type MeasurementType = 'eoc' | 'tag';

/** Active tag collection KPIs; first three are portions (0–100); impressions is a count. */
export interface ActiveMeasurementMetrics {
  devicePercentage: number;
  siteOrAppPercent: number;
  adUnitPercent: number;
  impressions: number;
}

export interface Measurement {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  advertiser: string;
  brand: string | null;
  status: MeasurementStatus;
  type: MeasurementType;
  brandClassification?: BrandClassification;
  brandStandings?: BrandStandings;
  brandLifts?: BrandLifts;
  benchmarks?: Benchmark[];
  impressions?: number | null;
  reach?: number | null;
  frequency?: number | null;
  createdAt: string;
  script?: string;
  creatives?: Creative[];
  /** Executive summary text when no benchmark is selected; from API. */
  executiveSummaryText?: string | null;
}

export interface CreativeWithExternalId {
  file?: File;
  previewUrl?: string;
  externalId: string;
}

/** Tenant-wide status KPIs; same rules as API list (effective status for tag + dates). */
export interface MeasurementStatusCounts {
  upcoming: number;
  active: number;
  reported: number;
  failed: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  /** All measurements for the tenant, not limited to the current page. */
  statusCounts?: MeasurementStatusCounts | null;
}

/** Response from POST create (202 = EOC accepted for background processing). */
export interface CreateMeasurementResult {
  measurementId: string;
  acceptedAsync: boolean;
}

const appendCreativesFormData = (
  formData: FormData,
  creatives: CreativeWithExternalId[]
) => {
  creatives.forEach((creative) => {
    if (creative.file) {
      formData.append('creatives', creative.file);
      formData.append('creativeExternalIds', creative.externalId);
    } else if (creative.previewUrl) {
      formData.append('previewUrls', creative.previewUrl);
      formData.append('previewUrlExternalIds', creative.externalId);
    }
  });
};

// Create a new measurement
export const createMeasurement = async (
  name: string,
  advertiser: string,
  brand: string | null,
  creatives: CreativeWithExternalId[],
  endOfCampaignReport: File
): Promise<CreateMeasurementResult> => {
  const formData = new FormData();
  
  // Add creatives with their ad manager IDs
  appendCreativesFormData(formData, creatives);
  
  // Add other fields
  formData.append('name', name);
  formData.append('advertiser', advertiser);
  if (brand) {
    formData.append('brand', brand);
  }
  formData.append('endOfCampaignReport', endOfCampaignReport);
  
  const response = await apiPost(`${API_BASE_URL}/create`, formData);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to create measurement');
  }

  const body = (await response.json()) as { measurementId: string };
  return {
    measurementId: body.measurementId,
    acceptedAsync: response.status === 202,
  };
};

// Create a new measurement via tag collection (without EOC report)
export const createMeasurementViaTag = async (
  name: string,
  advertiser: string,
  brand: string | null,
  startDate: string,
  endDate: string,
  creatives: CreativeWithExternalId[]
): Promise<CreateMeasurementResult> => {
  const formData = new FormData();
  appendCreativesFormData(formData, creatives);
  
  // Add other fields
  formData.append('name', name);
  formData.append('advertiser', advertiser);
  formData.append('startDate', startDate);
  formData.append('endDate', endDate);
  if (brand) {
    formData.append('brand', brand);
  }
  
  const response = await apiPost(`${API_BASE_URL}/create-via-tag`, formData);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to create measurement');
  }
  
  const body = (await response.json()) as { measurementId: string };
  return {
    measurementId: body.measurementId,
    acceptedAsync: response.status === 202,
  };
};

export const addCreativesToMeasurement = async (
  id: string,
  creatives: CreativeWithExternalId[]
): Promise<void> => {
  const formData = new FormData();
  appendCreativesFormData(formData, creatives);

  const response = await apiPost(`${API_BASE_URL}/${id}/creatives`, formData);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to add creatives');
  }
};

// Get measurements with pagination
export const getMeasurements = async (
  page: number = 1,
  pageSize: number = 20
): Promise<PagedResult<Measurement>> => {
  const response = await apiGet(`${API_BASE_URL}?page=${page}&pageSize=${pageSize}`);

  if (!response.ok) {
    throw new Error('Failed to fetch measurements');
  }

  return await response.json();
};

// Get a specific measurement by ID
export const getMeasurementById = async (id: string): Promise<Measurement | null> => {
  const response = await apiGet(`${API_BASE_URL}/${id}`);
  
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('Failed to fetch measurement');
  }
  
  return await response.json();
};

/** Dummy KPIs for active tag measurements (collection data completeness). */
export const getActiveMeasurementMetrics = async (
  id: string
): Promise<ActiveMeasurementMetrics> => {
  const response = await apiGet(`${API_BASE_URL}/${id}/ActiveMeasurementMetrics`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Measurement not found');
    }
    const text = await response.text();
    throw new Error(text || 'Failed to load collection metrics');
  }

  return await response.json();
};

/** Query params for GET .../measurement/{id}/tag-script (mirrors backend enums). */
export type TagMacroProfile = 'GoogleAdManager' | 'DV360' | 'CM360';
export type TagCreativeMedium = 'Display' | 'CTV';
export type TagExposureType = 'Javascript' | 'Image' | 'VastImpression' | 'VastComplete';

export interface GetMeasurementTagScriptOptions {
  macroProfile?: TagMacroProfile;
  creativeMedium?: TagCreativeMedium;
  exposureType?: TagExposureType;
}

/** Plain tracking URL from GET .../measurement/{id}/tag-script (no HTML wrapper). */
export const getMeasurementTagUrl = async (
  id: string,
  options?: GetMeasurementTagScriptOptions
): Promise<string> => {
  const qs = new URLSearchParams();
  qs.set('macroProfile', options?.macroProfile ?? 'GoogleAdManager');
  qs.set('creativeMedium', options?.creativeMedium ?? 'Display');
  qs.set('exposureType', options?.exposureType ?? 'Javascript');
  const response = await apiGet(`${API_BASE_URL}/${id}/tag-script?${qs.toString()}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Measurement not found');
    }
    if (response.status === 400) {
      const text = await response.text();
      throw new Error(text || 'Invalid tag script options');
    }
    throw new Error('Failed to load tag URL');
  }

  return response.text();
};

// Get creatives (files) for a specific measurement by ID
export const getCreativesByMeasurementId = async (id: string): Promise<Creative[]> => {
  const response = await apiGet(`/api/file/measurement/${id}?type=Creative`);

  if (!response.ok) {
    if (response.status === 404) {
      return [];
    }
    throw new Error('Failed to fetch creatives');
  }

  const files = await response.json();

  // Infer file type from original file name extension
  const inferType = (fileName: string): 'image' | 'video' | 'other' => {
    const lower = fileName.toLowerCase();
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(lower)) {
      return 'image';
    }
    if (/\.(mp4|webm|mov|avi|mkv)$/.test(lower)) {
      return 'video';
    }
    return 'other';
  };

  return (files as any[]).map(file => {
    const originalFileName = file.originalFileName as string;
    return {
      id: file.id as string,
      name: originalFileName,
      type: inferType(originalFileName),
      url: (file.signedUrl as string) ?? '',
      externalId: (file.externalId as string) ?? ''
    } as Creative;
  });
};

// Update a measurement
export const updateMeasurement = async (id: string, updates: Partial<Measurement>): Promise<Measurement | null> => {
  const response = await apiPut(`${API_BASE_URL}/${id}`, updates);
  
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('Failed to update measurement');
  }
  
  return await response.json();
};

// Delete a measurement
export const deleteMeasurement = async (id: string): Promise<boolean> => {
  const response = await apiDelete(`${API_BASE_URL}/${id}`);

  return response.ok;
};

// Export a measurement as PDF (optionally scoped to a specific benchmark)
export const exportMeasurementPDF = async (id: string, benchmarkName?: string): Promise<Blob> => {
  const url = benchmarkName
    ? `${API_BASE_URL}/${id}/export/pdf?benchmarkName=${encodeURIComponent(benchmarkName)}`
    : `${API_BASE_URL}/${id}/export/pdf`;
  const response = await apiGet(url);
  if (!response.ok) {
    throw new Error(`Failed to export PDF: ${response.status}`);
  }
  return response.blob();
};

// Export a measurement as Excel
export const exportMeasurementExcel = async (id: string): Promise<Blob> => {
  const response = await apiGet(`${API_BASE_URL}/${id}/export/excel`);
  if (!response.ok) {
    throw new Error(`Failed to export Excel: ${response.status}`);
  }
  return response.blob();
};

// Trigger a browser download from a Blob
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
